const SPINNER_MS = 8_000;
const BACKOFF_MIN_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;
const POLL_OK_MS = 4_000;

const DEGRADACAO = {
  titulo: 'Atualização em tempo real pausada',
  corpo: 'A página continua segura. O status será consultado automaticamente.',
  proximo_passo: 'Você pode seguir trabalhando. Não é necessário recarregar agora.',
} as const;

const SESSAO = {
  titulo: 'Sua sessão encerrou',
  corpo: 'Paramos de consultar o status para não misturar empresas ou usuários.',
  proximo_passo: 'Entre de novo para acompanhar esta nota. Nada foi reenviado à Receita.',
} as const;

const PERMISSAO = {
  titulo: 'Você não tem permissão para este registro',
  corpo: 'Não mostramos detalhes desta nota neste acesso.',
  proximo_passo: 'Confira a empresa ativa ou peça acesso a quem administra o escritório.',
} as const;

const STATUS_INCOMPLETO =
  'A resposta de status veio incompleta. Continuamos consultando, sem reenviar a nota.';

type Aviso = {
  titulo: string;
  corpo: string;
  proximo_passo: string;
  correlation_id?: string;
};

const watches = new Map<string, AbortController>();

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isAbort(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

function isTerminalStatus(status: string): boolean {
  const normalized = status.trim().toUpperCase();
  return (
    normalized === 'AUTHORIZED' ||
    normalized === 'REJECTED_FINAL' ||
    normalized === 'CANCELLED_BEFORE_SUBMISSION' ||
    normalized === 'NOT_FOUND_CONFIRMED' ||
    normalized === 'MANUAL_REVIEW'
  );
}

function jitter(attempt: number): number {
  const cap = Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** Math.max(0, attempt));
  return Math.floor(Math.random() * cap);
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}

function parseRetryAfter(header: string | null): number | null {
  if (!header) {
    return null;
  }
  const seconds = Number.parseInt(header, 10);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return Math.min(BACKOFF_MAX_MS, seconds * 1000);
  }
  const date = Date.parse(header);
  if (Number.isNaN(date)) {
    return null;
  }
  return Math.min(BACKOFF_MAX_MS, Math.max(0, date - Date.now()));
}

function liberarEspera(root: ParentNode): void {
  root.querySelectorAll('[aria-busy="true"]').forEach((el) => {
    el.setAttribute('aria-busy', 'false');
  });
  root.querySelectorAll('[data-spinner]').forEach((el) => {
    if (el instanceof HTMLElement) {
      el.hidden = true;
    }
  });
  document.documentElement.classList.remove('is-sse-pending');
}

function correlationFrom(root: HTMLElement, extra?: string): string {
  return extra?.trim() || root.getAttribute('data-correlation-id')?.trim() || '';
}

function mostrarAviso(root: HTMLElement, aviso: Aviso): void {
  let banner = root.querySelector<HTMLElement>('[data-sse-degradacao]');
  if (!banner) {
    banner = document.createElement('aside');
    banner.setAttribute('data-sse-degradacao', '');
    banner.className = 'banner-degradacao';
    banner.setAttribute('role', 'status');
    banner.setAttribute('aria-live', 'polite');
    root.prepend(banner);
  }
  banner.hidden = false;
  const correlation = correlationFrom(root, aviso.correlation_id);
  banner.replaceChildren();
  const title = document.createElement('strong');
  title.textContent = aviso.titulo;
  const corpo = document.createElement('p');
  corpo.textContent = aviso.corpo;
  const proximo = document.createElement('p');
  proximo.textContent = aviso.proximo_passo;
  banner.append(title, corpo, proximo);
  if (correlation !== '') {
    const codigo = document.createElement('p');
    codigo.className = 'banner-degradacao__correlacao';
    codigo.textContent = 'Código de correlação: ';
    const code = document.createElement('code');
    code.textContent = correlation;
    codigo.append(code);
    banner.append(codigo);
  }
}

function anunciar(root: HTMLElement, texto: string): void {
  let live = root.querySelector<HTMLElement>('[data-sse-live]');
  if (!live) {
    live = document.createElement('p');
    live.setAttribute('data-sse-live', '');
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');
    root.append(live);
  }
  live.textContent = texto;
}

function marcarTerminal(root: HTMLElement): void {
  root.dataset['sseDone'] = '1';
  liberarEspera(root);
}

function aplicarHtml(root: HTMLElement, html: string): void {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const origem = parsed.querySelector('[data-nota-live]') ?? parsed.body;
  const destino = root.querySelector('[data-nota-live]') ?? root;
  if (!origem) {
    return;
  }
  destino.innerHTML = origem.innerHTML;
  if (!destino.querySelector('[aria-busy="true"], [data-spinner]:not([hidden])')) {
    liberarEspera(root);
  }
}

function aplicarJson(root: HTMLElement, data: unknown): void {
  if (!isRecord(data)) {
    return;
  }
  if (typeof data['html'] === 'string') {
    aplicarHtml(root, data['html']);
  }
  if (typeof data['etag'] === 'string') {
    root.dataset['etag'] = data['etag'];
  }
  if (typeof data['correlation_id'] === 'string') {
    root.setAttribute('data-correlation-id', data['correlation_id']);
  }
  if (typeof data['status'] === 'string' && isTerminalStatus(data['status'])) {
    marcarTerminal(root);
  }
  if (data['done'] === true) {
    marcarTerminal(root);
  }
  if (
    typeof data['titulo'] === 'string' &&
    typeof data['corpo'] === 'string' &&
    typeof data['proximo_passo'] === 'string'
  ) {
    mostrarAviso(root, {
      titulo: data['titulo'],
      corpo: data['corpo'],
      proximo_passo: data['proximo_passo'],
      correlation_id: typeof data['correlation_id'] === 'string' ? data['correlation_id'] : '',
    });
  }
  if (typeof data['href'] === 'string' && data['href'].startsWith('/notas/')) {
    let link = root.querySelector<HTMLAnchorElement>('[data-nota-href]');
    if (!link) {
      link = document.createElement('a');
      link.className = 'btn-primary follow';
      link.setAttribute('data-nota-href', '');
      link.textContent = 'Abrir nota autorizada';
      root.append(link);
    }
    link.href = data['href'];
  }
}

function aplicarTexto(root: HTMLElement, text: string, contentType: string | null): void {
  const trimmed = text.trim();
  if (trimmed === '') {
    return;
  }
  const ct = contentType ?? '';
  if (ct.includes('json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      aplicarJson(root, JSON.parse(trimmed) as unknown);
    } catch (error) {
      void error;
      anunciar(root, STATUS_INCOMPLETO);
    }
    return;
  }
  if (trimmed.startsWith('<')) {
    aplicarHtml(root, text);
  }
}

function tratarEventoSse(root: HTMLElement, raw: string, source: EventSource): void {
  aplicarTexto(root, raw, raw.trim().startsWith('{') ? 'application/json' : 'text/html');
  if (root.dataset['sseDone'] === '1') {
    source.close();
  }
}

function degradar(root: HTMLElement, correlationId?: string): void {
  liberarEspera(root);
  mostrarAviso(root, {
    titulo: DEGRADACAO.titulo,
    corpo: DEGRADACAO.corpo,
    proximo_passo: DEGRADACAO.proximo_passo,
    correlation_id: correlationId ?? '',
  });
}

async function pollLoop(
  root: HTMLElement,
  pollUrl: string,
  signal: AbortSignal,
): Promise<void> {
  let attempt = 0;
  let etag = root.dataset['etag'] ?? '';
  while (!signal.aborted && root.dataset['sseDone'] !== '1') {
    if (document.visibilityState === 'hidden') {
      await sleep(1000, signal);
      continue;
    }
    try {
      const headers = new Headers({
        Accept: 'text/html, application/json;q=0.9',
      });
      if (etag !== '') {
        headers.set('If-None-Match', etag);
      }
      const response = await fetch(pollUrl, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers,
        signal,
      });
      const nextTag = response.headers.get('ETag');
      if (nextTag) {
        etag = nextTag;
        root.dataset['etag'] = nextTag;
      }
      const correlation = response.headers.get('x-correlation-id') ?? '';
      if (correlation !== '') {
        root.setAttribute('data-correlation-id', correlation);
      }
      if (response.status === 304) {
        attempt = 0;
        await sleep(POLL_OK_MS, signal);
        continue;
      }
      if (response.status === 401) {
        liberarEspera(root);
        mostrarAviso(root, { ...SESSAO, correlation_id: correlation });
        return;
      }
      if (response.status === 403 || response.status === 404) {
        liberarEspera(root);
        mostrarAviso(root, { ...PERMISSAO, correlation_id: correlation });
        return;
      }
      const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
      const text = await response.text();
      if (!response.ok) {
        aplicarTexto(root, text, response.headers.get('content-type'));
        attempt += 1;
        await sleep(retryAfter ?? jitter(attempt), signal);
        continue;
      }
      aplicarTexto(root, text, response.headers.get('content-type'));
      if (root.dataset['sseDone'] === '1') {
        return;
      }
      attempt = 0;
      await sleep(retryAfter ?? POLL_OK_MS, signal);
    } catch (error) {
      if (signal.aborted || isAbort(error)) {
        return;
      }
      attempt += 1;
      degradar(root);
      await sleep(jitter(attempt), signal);
    }
  }
}

function abrirSse(
  root: HTMLElement,
  sseUrl: string,
  pollUrl: string,
  signal: AbortSignal,
): boolean {
  if (!('EventSource' in window)) {
    return false;
  }
  try {
    const source = new EventSource(sseUrl);
    let polling = false;
    const irParaPolling = (): void => {
      if (polling || signal.aborted || root.dataset['sseDone'] === '1') {
        return;
      }
      polling = true;
      source.close();
      degradar(root);
      void pollLoop(root, pollUrl, signal);
    };
    source.addEventListener('message', (event) => {
      tratarEventoSse(root, event.data, source);
    });
    source.addEventListener('status', (event) => {
      tratarEventoSse(root, event.data, source);
    });
    source.addEventListener('nota', (event) => {
      tratarEventoSse(root, event.data, source);
    });
    source.addEventListener('error', () => {
      irParaPolling();
    });
    signal.addEventListener(
      'abort',
      () => {
        source.close();
      },
      { once: true },
    );
    return true;
  } catch (error) {
    void error;
    return false;
  }
}

async function watchRoot(root: HTMLElement, id: string, signal: AbortSignal): Promise<void> {
  const spinnerTimer = window.setTimeout(() => {
    liberarEspera(root);
    if (root.dataset['sseDone'] !== '1') {
      anunciar(
        root,
        'Consultando o status desta nota. O envio já foi registrado; não reenviamos.',
      );
    }
  }, SPINNER_MS);
  signal.addEventListener(
    'abort',
    () => {
      window.clearTimeout(spinnerTimer);
    },
    { once: true },
  );

  const sseUrl = root.getAttribute('data-sse-url')?.trim() || `/sse/notas/${encodeURIComponent(id)}`;
  const pollUrl = root.getAttribute('data-poll-url')?.trim() || `/notas/${encodeURIComponent(id)}`;

  const sseOk = abrirSse(root, sseUrl, pollUrl, signal);
  if (!sseOk) {
    degradar(root);
    await pollLoop(root, pollUrl, signal);
  }
}

export function bindNotaSse(): void {
  for (const controller of watches.values()) {
    controller.abort();
  }
  watches.clear();

  document.querySelectorAll<HTMLElement>('[data-sse-nota]').forEach((root) => {
    const id = root.getAttribute('data-sse-nota')?.trim() || root.getAttribute('data-nota-id')?.trim() || '';
    if (id === '') {
      return;
    }
    const controller = new AbortController();
    watches.set(`${id}:${pollKey(root)}`, controller);
    void watchRoot(root, id, controller.signal);
  });
}

function pollKey(root: HTMLElement): string {
  return root.getAttribute('data-poll-url')?.trim() || root.getAttribute('data-sse-url')?.trim() || '';
}
